// === Configuration publicité Gazette ===
// Source unique pour les flags, fréquences, IDs et helpers d'insertion.
// Pour le moment, les pubs sont des MOCKS — pas d'AdMob/AdSense branché.

import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

export const ADS_ENABLED = import.meta.env.VITE_ADS_ENABLED === 'true';

export const AD_SLOTS = {
  discover_native: true,  // Carte native dans Discover (entre "À voir aussi" et "Encore X idées")
  discover_list: true,    // Item compact dans la liste "Encore X idées"
  detail_banner: true,    // Bannière sur EventDetail
  explorer_list: true,    // Item sponsorisé dans la liste du bottom sheet Explorer
} as const;

export type AdSlot = keyof typeof AD_SLOTS;

// Fréquence d'insertion dans les listes longues
export const AD_FREQUENCY = {
  discover_list_interval: 8,  // 1 pub toutes les 8 positions dans "Encore X idées"
  discover_list_first_at: 3,  // Première pub à la position 3 (liste plus courte qu'Explorer)
  explorer_list_interval: 8,  // 1 pub toutes les 8 positions dans Explorer
  explorer_first_at: 7,       // Première pub à la position 7
} as const;

// IDs AdMob — placeholders. À renseigner quand on branchera AdMob/AdSense.
export const AD_UNIT_IDS = {
  discover_native: import.meta.env.DEV
    ? 'ca-app-pub-3940256099942544/3986624511' // Test ID Google
    : 'ca-app-pub-XXXXX/YYYYY',
  detail_banner: import.meta.env.DEV
    ? 'ca-app-pub-3940256099942544/6300978111'
    : 'ca-app-pub-XXXXX/ZZZZZ',
  explorer_native: import.meta.env.DEV
    ? 'ca-app-pub-3940256099942544/3986624511'
    : 'ca-app-pub-XXXXX/WWWWW',
} as const;

// ---------------------------------------------------------------------------
// First session — pas de pub au tout premier lancement
// ---------------------------------------------------------------------------

const SESSION_COUNT_KEY = 'gazette:sessionCount';
const FIRST_SESSION_DONE_KEY = 'gazette:hasSeenFirstSession';

/**
 * À appeler une fois au démarrage de l'app (App.tsx).
 * Incrémente un compteur d'ouvertures. À partir de la 2ᵉ ouverture, on considère
 * que l'utilisateur a vu la première session et on autorise les pubs.
 */
export function bootstrapAdSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(SESSION_COUNT_KEY);
    const next = (raw ? parseInt(raw, 10) || 0 : 0) + 1;
    localStorage.setItem(SESSION_COUNT_KEY, String(next));
    if (next >= 2) localStorage.setItem(FIRST_SESSION_DONE_KEY, 'true');
  } catch {
    // localStorage indispo (mode privé) — on ignore, les pubs ne s'afficheront simplement pas.
  }
}

/** Indique si on a passé la première session (= 2ᵉ ouverture ou plus). */
export function hasSeenFirstSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(FIRST_SESSION_DONE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Force le flag "première session vue" (utile pour debug). */
export function markFirstSessionComplete(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(FIRST_SESSION_DONE_KEY, 'true');
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Hook React — décide si on affiche un slot pub donné
// ---------------------------------------------------------------------------

/**
 * Retourne `true` si on doit afficher la pub pour ce slot (tous les flags + first session OK).
 * Utilisé dans les composants AdXxx pour court-circuiter le rendu.
 */
export function useShouldShowAd(slot: AdSlot): boolean {
  const [eligible, setEligible] = useState(false);
  useEffect(() => {
    if (!ADS_ENABLED) return;
    if (!AD_SLOTS[slot]) return;
    if (!hasSeenFirstSession()) return;
    setEligible(true);
  }, [slot]);
  return eligible;
}

// ---------------------------------------------------------------------------
// Helper d'interleaving (insertion de marqueurs pub dans une liste)
// ---------------------------------------------------------------------------

export type AdMarker = { __ad: true; key: string };

export function isAdMarker(item: unknown): item is AdMarker {
  return typeof item === 'object' && item !== null && (item as AdMarker).__ad === true;
}

/**
 * Intercale des marqueurs pub dans un tableau d'items.
 * - Première pub à la position `firstAt`
 * - Puis une pub toutes les `interval` positions ensuite
 * Les indices des items originaux sont préservés (les pubs s'insèrent ENTRE).
 *
 * Exemple : items = [a,b,c,d,e,f,g,h,i,j], firstAt=3, interval=4
 * → [a,b,c, AD, d,e,f,g, AD, h,i,j]
 */
export function interleaveAds<T>(
  items: T[],
  interval: number,
  firstAt: number,
): (T | AdMarker)[] {
  if (interval <= 0 || items.length === 0) return items;
  const out: (T | AdMarker)[] = [];
  let adCount = 0;
  for (let i = 0; i < items.length; i++) {
    if (i === firstAt || (i > firstAt && (i - firstAt) % interval === 0)) {
      out.push({ __ad: true, key: `ad-${adCount++}` });
    }
    out.push(items[i]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Mock content — pool d'annonceurs factices pour développer les composants
// avant le branchement réel d'AdMob/AdSense.
// ---------------------------------------------------------------------------

export type MockAd = {
  id: string;
  advertiser: string; // Nom de l'annonceur (s'affiche en bas de carte)
  headline: string;   // Titre/baseline de la pub (s'affiche comme un titre d'event)
  city: string;       // Ville mock pour le format Explorer
  url: string;        // Lien cliquable (mock)
  tone: 'terre' | 'champagne' | 'prune' | 'basilic' | 'velours' | 'encre-nuit';
  pattern: 'parasols' | 'confetti' | 'curves' | 'waves' | 'organic' | 'damier';
};

const MOCK_ADS: MockAd[] = [
  {
    id: 'mock-1',
    advertiser: 'Bistrot Lafayette',
    headline: "Cuisine de saison, à deux pas de chez vous",
    city: "Clermont-l'Hérault",
    url: 'https://example.com/bistrot',
    tone: 'terre',
    pattern: 'curves',
  },
  {
    id: 'mock-2',
    advertiser: 'Librairie Le Vent',
    headline: 'Rentrée littéraire — sélection automne',
    city: 'Montpellier',
    url: 'https://example.com/librairie',
    tone: 'prune',
    pattern: 'damier',
  },
  {
    id: 'mock-3',
    advertiser: 'Cave du Salagou',
    headline: 'Dégustations gratuites tous les samedis',
    city: 'Octon',
    url: 'https://example.com/cave',
    tone: 'velours',
    pattern: 'organic',
  },
  {
    id: 'mock-4',
    advertiser: 'Atelier Champagne',
    headline: 'Cours de poterie, débutants bienvenus',
    city: 'Lodève',
    url: 'https://example.com/atelier',
    tone: 'champagne',
    pattern: 'waves',
  },
  {
    id: 'mock-5',
    advertiser: 'Festival du Lac',
    headline: 'Édition 2026 — billetterie ouverte',
    city: 'Salagou',
    url: 'https://example.com/festival',
    tone: 'encre-nuit',
    pattern: 'confetti',
  },
];

/**
 * Retourne une pub mock déterministe basée sur une clé (pour la stabilité des renders).
 * Si pas de clé, en pioche une aléatoire.
 */
export function getMockAd(key?: string): MockAd {
  if (!key) return MOCK_ADS[Math.floor(Math.random() * MOCK_ADS.length)];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return MOCK_ADS[Math.abs(h) % MOCK_ADS.length];
}
